import React, { useState, useEffect } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import StatusBadge from '../components/StatusBadge';
import { format } from 'date-fns';
import { EyeIcon } from '@heroicons/react/24/outline';
import Modal from '../components/Modal';

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 5,
    total: 0
  });

  useEffect(() => {
    fetchPayments();
  }, [pagination.page, pagination.per_page]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/payments/', {
        params: {
          page: pagination.page,
          per_page: pagination.per_page
        }
      });
      setPayments(response.data.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      header: 'Payment #',
      accessorKey: 'payment_number',
    },
    {
      header: 'Date',
      accessorKey: 'payment_date',
      cell: ({ getValue }) => format(new Date(getValue()), 'dd MMM yyyy')
    },
    {
      header: 'Transaction',
      accessorKey: 'transaction_number',
    },
    {
      header: 'Shop Owner',
      accessorKey: 'shop_owner_name',
    },
    {
      header: 'Amount',
      accessorKey: 'amount',
      cell: ({ getValue }) => `ZMW ${getValue()?.toLocaleString() || 0}`
    },
    {
      header: 'Method',
      accessorKey: 'payment_method',
      cell: ({ getValue }) => getValue()?.replace('_', ' ').toUpperCase() || '-'
    },
    {
      header: 'Reference',
      accessorKey: 'reference_number',
      cell: ({ getValue }) => getValue() || '-'
    },
    {
      header: 'Received By',
      accessorKey: 'receiver_name',
    },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <button
          onClick={() => {
            setSelectedPayment(row.original);
            setShowModal(true);
          }}
          className="text-blue-600 hover:text-blue-900"
        >
          <EyeIcon className="h-5 w-5" />
        </button>
      )
    }
  ];

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Payments</h1>
          <p className="mt-2 text-sm text-gray-700">
            View all payment transactions.
          </p>
        </div>
      </div>

      <div className="mt-8">
        <DataTable
          data={payments}
          columns={columns}
          loading={loading}
          pageSize={pagination.per_page}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          onPageSizeChange={(per_page) => setPagination(prev => ({ ...prev, per_page, page: 1 }))}
        />
      </div>

      {/* Payment Details Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedPayment(null);
        }}
        title="Payment Details"
      >
        {selectedPayment && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Payment Number</label>
                <p className="font-medium">{selectedPayment.payment_number}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Date</label>
                <p className="font-medium">{format(new Date(selectedPayment.payment_date), 'dd MMM yyyy')}</p>
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-500">Transaction</label>
              <p className="font-medium">{selectedPayment.transaction_number}</p>
            </div>

            <div>
              <label className="text-sm text-gray-500">Shop Owner</label>
              <p className="font-medium">{selectedPayment.shop_owner_name}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-500">Amount</label>
                <p className="font-medium text-lg">ZMW {selectedPayment.amount?.toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Payment Method</label>
                <p className="font-medium capitalize">{selectedPayment.payment_method?.replace('_', ' ')}</p>
              </div>
            </div>

            {selectedPayment.reference_number && (
              <div>
                <label className="text-sm text-gray-500">Reference Number</label>
                <p className="font-medium">{selectedPayment.reference_number}</p>
              </div>
            )}

            {selectedPayment.notes && (
              <div>
                <label className="text-sm text-gray-500">Notes</label>
                <p className="text-gray-700">{selectedPayment.notes}</p>
              </div>
            )}

            <div>
              <label className="text-sm text-gray-500">Received By</label>
              <p className="font-medium">{selectedPayment.receiver_name}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Payments;